// src/auth/tokenProvider.ts - the ONE place the console's access token comes
// from.
//
// Every request the console sends and every socket handshake asks here for the
// current bearer, instead of copying a token that was written somewhere once.
// The token used to be put on the axios defaults at sign-in and never renewed,
// so the first request after it expired read as "Invalid credentials"; and two
// of the three sockets baked the sign-in token into their connect options, so a
// reconnect after a laptop slept presented an expired token on every retry.
//
// The provider holds no token of its own. It asks a SOURCE, registered by the
// AuthProvider for the session it manages: today the identity SDK's silent
// token call, which keeps the access token in memory and renews it once it has
// expired. Nothing here writes to web storage, and nothing here logs a token.
//
// Single flight: when several callers ask for a renewal at once (five requests
// refused 401 in the same tick, three sockets reconnecting as a laptop wakes)
// exactly one renewal runs and every caller gets its answer. Today a renewal is
// a hidden-iframe authorize round trip to the identity provider, so N of them
// at once is N round trips for one token; with a rotating refresh credential
// (the staff sign-in that follows this), N renewals would each spend the
// credential and reuse of a spent one reads as theft and ends the session.

export interface TokenRequest {
  /**
   * Renew regardless of what the source holds: the api has just refused the
   * current token, so a cached copy of it is no use.
   */
  forceRefresh: boolean;
}

export type TokenSource = (request: TokenRequest) => Promise<string>;

/** There is no session to take a token from, or the source answered without one. */
export class NoSessionError extends Error {
  constructor() {
    super("No signed-in session.");
    this.name = "NoSessionError";
  }
}

let source: TokenSource | null = null;
let inFlightRead: Promise<string> | null = null;
let inFlightRefresh: Promise<string> | null = null;

/**
 * Register the session's token source, or `null` once it is signed out. A call
 * in flight belongs to the session being replaced; new callers ask the new one.
 */
export const setTokenSource = (next: TokenSource | null): void => {
  source = next;
  inFlightRead = null;
  inFlightRefresh = null;
};

export const hasSession = (): boolean => source !== null;

const askSource = async (
  active: TokenSource,
  request: TokenRequest
): Promise<string> => {
  const token = await active(request);
  if (typeof token !== "string" || token.length === 0) {
    throw new NoSessionError();
  }
  return token;
};

/** The current access token, renewed by the source if it has expired. */
export const getToken = (): Promise<string> => {
  const active = source;
  if (!active) return Promise.reject(new NoSessionError());
  // A renewal under way answers with a newer token than any read would. If it
  // fails (the identity host briefly unreachable), the read still asks for the
  // token the source holds, which may well be valid: the renewal was for one
  // caller's refused request, not proof that every token is dead.
  if (inFlightRefresh) {
    return inFlightRefresh.catch(() =>
      askSource(active, { forceRefresh: false })
    );
  }
  if (inFlightRead) return inFlightRead;
  const read = askSource(active, { forceRefresh: false }).finally(() => {
    if (inFlightRead === read) inFlightRead = null;
  });
  inFlightRead = read;
  return read;
};

/**
 * A renewed access token, because the api refused the current one. Callers
 * asking at the same time share the one renewal.
 */
export const refreshToken = (): Promise<string> => {
  if (inFlightRefresh) return inFlightRefresh;
  const active = source;
  if (!active) return Promise.reject(new NoSessionError());
  // Let a read in flight settle first. The identity SDK coalesces concurrent
  // token calls on one key regardless of the cache mode, so a forced renewal
  // started beside a cached read would join it and come back with the very
  // token the api just refused.
  const renew = () => askSource(active, { forceRefresh: true });
  const refresh = (
    inFlightRead ? inFlightRead.catch(() => undefined).then(renew) : renew()
  ).finally(() => {
    if (inFlightRefresh === refresh) inFlightRefresh = null;
  });
  inFlightRefresh = refresh;
  return refresh;
};

/**
 * The Authorization header value a request leaves with, or undefined when
 * there is no session or it cannot produce a token right now. Callers send
 * the request without a bearer in both cases and let the api's 401 be the
 * answer, as it always was. The cause is not surfaced here: the source owns
 * what to do about it, and nothing here may ever print a token.
 */
export const currentBearer = async (): Promise<string | undefined> => {
  if (!hasSession()) return undefined;
  try {
    return `Bearer ${await getToken()}`;
  } catch {
    return undefined;
  }
};
